import React from "react";
import { Navigate, RouteObject, useLocation } from "react-router-dom";

import AuthPageContainer from "../components/Layout/AuthPageContainer";
import Signup from "../pages/Auth/Signup";
import Login from "../pages/Auth/Login";
import ResetPassword from "../pages/Auth/ResetPassword";
import SelectIntegration from "../pages/Auth/SelectIntegration";
import Dashboard from "../pages/Dashboard";
import OutreachRedirect from "../pages/Outreach/OutreachRedirect";
import OutreachInstallRedirect from "../pages/Outreach/OutreachInstallRedirect";
import HubSpotRedirect from "../pages/HubSpot/HubSpotRedirect";
import ReconnectHubSpot from "../pages/HubSpot/ReconnectHubSpot";
import HubSpotLinkedIn from "../pages/HubSpot/HubSpotLinkedIn";
import Onboarding from "../pages/Onboarding/Onboarding";
import WaitForOnboarding from "../pages/WaitForOnboarding";
import Insights from "../pages/Insights/Insights";
import LinkedInConversations from "../pages/Outreach/LinkedInConversations";
import LinkedInCards from "../pages/Outreach/LinkedInCards";
import ConnectExtension from "../pages/ConnectExtension";
import McpAuthorize from "../pages/Mcp/McpAuthorize";
import DevCrmPreview from "../pages/DevCrmPreview";
import DevHubSpotKit from "../pages/DevHubSpotKit";
import { isProduction } from "./config";
import { asCrm, withCrm } from "../components/CrmUi/authCrm";

/** Signed out, any other URL goes to sign in, keeping the website's ?crm=. */
const RedirectToLogin = () => {
  const { search } = useLocation();
  const crm = asCrm(new URLSearchParams(search).get("crm"));
  return <Navigate to={crm ? withCrm("/auth/login", crm) : "/auth/login"} replace />;
};

// Development and staging only: the dashboard with a made-up user, in either
// CRM's look (/dev/crm-preview?crmTheme=hubspot). Signed in or not.
const devRoutes = isProduction
  ? []
  : [
      {
        path: "/dev/crm-preview",
        element: <DevCrmPreview />,
      },
      {
        // Every HubSpot kit part in each state (/dev/hubspot-kit?crmTheme=hubspot),
        // diffed against the live HubSpot captures by app/scripts/hubspot-visual.
        path: "/dev/hubspot-kit",
        element: <DevHubSpotKit />,
      },
    ];

const nonAuthenticatedRoutes = [
  ...devRoutes,
  {
    path: "/auth",
    element: <AuthPageContainer />,
    children: [
      {
        path: "/auth/signup",
        element: <Signup />,
      },
      {
        path: "/auth/login",
        element: <Login />,
      },
      {
        path: "/auth/reset-password",
        element: <ResetPassword />,
      },
    ],
  },
  {
    path: "/view/:outreachOrganizationId",
    element: <Insights />,
  },
  {
    // Outreach prospect TAB extension: every conversation the team has had.
    path: "/outreach/linkedin/conversations",
    element: <LinkedInConversations variant="tab" />,
  },
  {
    // Outreach prospect TILE extension: the newest conversation, with a composer.
    path: "/outreach/linkedin/conversation",
    element: <LinkedInConversations variant="tile" />,
  },
  {
    // Outreach APPLICATION tab: every conversation across the team.
    path: "/outreach/linkedin/inbox",
    element: <LinkedInConversations variant="inbox" />,
  },
  {
    // Outreach prospect TILE extension: the LinkedIn profile card.
    path: "/outreach/linkedin/profile",
    element: <LinkedInCards variant="profile" />,
  },
  {
    // Outreach prospect TILE extension: the LinkedIn activity card.
    path: "/outreach/linkedin/activity",
    element: <LinkedInCards variant="activity" />,
  },
  {
    // HubSpot iframe modal (sidebar card): LinkedIn conversation or profile and activity.
    path: "/hubspot/linkedin",
    element: <HubSpotLinkedIn />,
  },

  {
    path: "/*",
    element: <RedirectToLogin />,
  },
];

const authenticatedRoutes = [
  ...devRoutes,
  {
    // Outreach prospect TAB extension: every conversation the team has had.
    path: "/outreach/linkedin/conversations",
    element: <LinkedInConversations variant="tab" />,
  },
  {
    // Outreach prospect TILE extension: the newest conversation, with a composer.
    path: "/outreach/linkedin/conversation",
    element: <LinkedInConversations variant="tile" />,
  },
  {
    // Outreach APPLICATION tab: every conversation across the team.
    path: "/outreach/linkedin/inbox",
    element: <LinkedInConversations variant="inbox" />,
  },
  {
    // Outreach prospect TILE extension: the LinkedIn profile card.
    path: "/outreach/linkedin/profile",
    element: <LinkedInCards variant="profile" />,
  },
  {
    // Outreach prospect TILE extension: the LinkedIn activity card.
    path: "/outreach/linkedin/activity",
    element: <LinkedInCards variant="activity" />,
  },
  {
    // HubSpot iframe modal (sidebar card): LinkedIn conversation or profile and activity.
    path: "/hubspot/linkedin",
    element: <HubSpotLinkedIn />,
  },

  {
    path: "/app",
    element: <AuthPageContainer />,
    children: [
      {
        path: "/app/dashboard",
        element: <Dashboard />,
      },
      {
        path: "/app/select-integration",
        element: <SelectIntegration />,
      },
      {
        path: "/app/wait-for-onboarding",
        element: <WaitForOnboarding />,
      },
      {
        path: "/app/continue-onboarding",
        element: <Onboarding />,
      },
      {
        path: "/app/connect-extension",
        element: <ConnectExtension />,
      },
      {
        path: "/app/reconnect-hubspot",
        element: <ReconnectHubSpot />,
      },
      {
        path: "/app",
        element: <Navigate to="/app/dashboard" replace />,
      },
    ],
  },
  {
    path: "/oauth/outreach",
    element: <OutreachRedirect />,
  },
  {
    // Outreach sends an admin here after they install boring's Outreach app (its setup URL).
    path: "/oauth/outreach-install",
    element: <OutreachInstallRedirect />,
  },
  {
    // An AI app (Claude, ChatGPT) connecting to boring's MCP connector: the consent page.
    path: "/mcp/authorize",
    element: <McpAuthorize />,
  },
  {
    path: "/oauth/hubspot",
    element: <HubSpotRedirect />,
  },
  {
    path: "/*",
    element: <Navigate to="/app/dashboard" replace />,
  },
];

export const getRoutes = (signedInUser: boolean): RouteObject[] =>
  signedInUser ? authenticatedRoutes : nonAuthenticatedRoutes;
